import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { saveOfferOcDocuments } from "../../../utils/api-offers";

function genId() {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeRow(doc = {}, index = 0) {
  return {
    id: doc.id || `row_${index}_${genId()}`,
    oc_number: doc.oc_number || "",
    file_url: doc.file_url || "",
    file_name: doc.file_name || "",
    file_type: doc.file_type || "",
    storage_path: doc.storage_path || "",
    created_at: doc.created_at || null,
    file: null,
  };
}

function fileLabel(row) {
  if (row?.file?.name) return row.file.name;
  if (row?.file_name) return row.file_name;
  if (row?.file_url) return "Archivo cargado";
  return "Sin archivo";
}

function guessPreviewKind(row) {
  const raw = String(
    row?.file?.type ||
      row?.file_type ||
      row?.file_name ||
      row?.file_url ||
      ""
  ).toLowerCase();

  if (raw.includes("pdf") || raw.endsWith(".pdf")) return "pdf";
  if (raw.includes("png") || raw.endsWith(".png")) return "image";
  if (raw.includes("jpg") || raw.includes("jpeg") || raw.endsWith(".jpg") || raw.endsWith(".jpeg")) {
    return "image";
  }
  if (raw.includes("webp") || raw.endsWith(".webp")) return "image";
  return raw ? "file" : "";
}

export default function OfferOCManagerModal({ open, onClose, offer, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewRowId, setPreviewRowId] = useState("");
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewKind, setPreviewKind] = useState("");
  const [previewName, setPreviewName] = useState("");

  useEffect(() => {
    if (!open) return;

    const docs = Array.isArray(offer?.oc_documents) ? offer.oc_documents : [];
    const nextRows = docs.length
      ? docs.map((doc, index) => normalizeRow(doc, index))
      : [normalizeRow({}, 0)];

    setRows(nextRows);
    setPreviewRowId(nextRows[0]?.id || "");
  }, [open, offer]);

  useEffect(() => {
    if (!open) {
      setPreviewSrc("");
      setPreviewKind("");
      setPreviewName("");
      return;
    }

    const row = rows.find((item) => item.id === previewRowId) || rows[0] || null;
    if (!row) {
      setPreviewSrc("");
      setPreviewKind("");
      setPreviewName("");
      return;
    }

    let objectUrl = "";
    const source = row.file ? (objectUrl = URL.createObjectURL(row.file)) : row.file_url || "";
    setPreviewSrc(source);
    setPreviewKind(guessPreviewKind(row));
    setPreviewName(row.file?.name || row.file_name || row.oc_number || "Vista previa");

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, rows, previewRowId]);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, normalizeRow({}, prev.length)]);
  };

  const removeRow = (id) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((row) => row.id !== id);
      if (previewRowId === id) {
        setPreviewRowId(next[0]?.id || "");
      }
      return next;
    });
  };

  const onFileChange = (id, file) => {
    updateRow(id, {
      file: file || null,
      file_name: file?.name || "",
      file_type: file?.type || "",
    });
    setPreviewRowId(id);
  };

  const selectedRow = rows.find((item) => item.id === previewRowId) || rows[0] || null;

  if (!open || !offer) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((row) => ({
        id: row.id,
        oc_number: String(row.oc_number || "").trim(),
        file_url: row.file_url || "",
        file_name: row.file_name || "",
        file_type: row.file_type || "",
        storage_path: row.storage_path || "",
        created_at: row.created_at || null,
        file: row.file || null,
      }));

      for (let i = 0; i < payload.length; i += 1) {
        const row = payload[i];
        if (!row.oc_number) {
          throw new Error(`La orden ${i + 1} necesita un numero.`);
        }
        if (!row.file && !row.file_url) {
          throw new Error(`La orden ${i + 1} necesita un archivo PDF o imagen.`);
        }
      }

      await saveOfferOcDocuments(offer, payload);
      await onSaved?.();
      onClose?.();
    } catch (e) {
      console.error("[OfferOCManagerModal] save:", e);
      alert(e?.message || "No se pudieron guardar las ordenes de compra.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[96] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-2xl border border-border/60 bg-background text-card-foreground shadow-industrial-lg"
          >
            <div className="flex items-start justify-between gap-4 border-b border-border/60 bg-background px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold">Ordenes de compra</h3>
                <p className="mt-1 text-xs text-muted">
                  {offer?.code || "Oferta"} · {offer?.client?.name || "Cliente"}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="h-9 rounded-md border border-orange-500/40 px-3 text-sm text-orange-400 hover:bg-orange-500/20 transition"
              >
                Cerrar
              </button>
            </div>

            <div className="max-h-[calc(92vh-140px)] overflow-auto p-5 space-y-4">
              <div className="rounded-xl border border-border/60 bg-background px-4 py-3 text-xs text-muted shadow-sm">
                Cada bloque guarda numero de OC y archivo asociado. La ultima OC cargada se refleja en la columna de estado de la oferta.
              </div>

              <div className="space-y-3">
                {rows.map((row, index) => (
                  <div key={row.id} className="rounded-xl border border-border/60 bg-background p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium">Orden de compra {index + 1}</div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPreviewRowId(row.id)}
                          className={`text-xs rounded-md border px-2 py-1 transition ${
                            previewRowId === row.id
                              ? "border-orange-500/40 bg-orange-500/20 text-orange-400"
                              : "border-orange-500/40 text-orange-400 hover:bg-orange-500/10"
                          }`}
                        >
                          Previsualizar
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(row.id)}
                          disabled={rows.length <= 1}
                          className="text-xs text-rose-300 hover:text-rose-200 disabled:opacity-40"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="block text-xs text-muted mb-1">N° de OC</label>
                        <input
                          className="field-lg w-full"
                          value={row.oc_number}
                          onChange={(e) => updateRow(row.id, { oc_number: e.target.value })}
                          placeholder="Ej: OC-1234 / SAP 56789 / etc."
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-muted mb-1">
                          Archivo PDF / JPG / PNG
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                          className="field-lg w-full file:mr-4 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-primary"
                          onChange={(e) => onFileChange(row.id, e.target.files?.[0] || null)}
                        />
                        <div className="mt-1 text-[11px] text-muted">{fileLabel(row)}</div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted">
                        {row.file_url ? (
                          <a
                            href={row.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md border border-orange-500/40 px-2 py-1 text-orange-400 hover:bg-orange-500/20 transition"
                          >
                            Ver archivo actual
                          </a>
                      ) : null}
                      {row.file_type ? <span>{row.file_type}</span> : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-border/60 bg-background p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">Vista previa</div>
                    <div className="text-xs text-muted">
                      {selectedRow
                        ? `Orden ${rows.findIndex((item) => item.id === selectedRow.id) + 1} · ${previewName}`
                        : "Selecciona una orden para previsualizar"}
                    </div>
                  </div>
                  <div className="text-xs text-muted">
                    {previewKind === "pdf"
                      ? "PDF"
                      : previewKind === "image"
                        ? "Imagen"
                        : previewKind || "Sin archivo"}
                  </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-lg border border-border/60 bg-background">
                  {previewSrc ? (
                    previewKind === "image" ? (
                      <img
                        src={previewSrc}
                        alt={previewName || "Vista previa de orden de compra"}
                        className="max-h-[420px] w-full object-contain bg-black"
                      />
                    ) : (
                      <iframe
                        title={previewName || "Vista previa de orden de compra"}
                        src={previewSrc}
                        className="h-[420px] w-full border-0 bg-white"
                      />
                    )
                  ) : (
                    <div className="flex h-[240px] items-center justify-center text-sm text-muted">
                      No hay archivo para previsualizar.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={addRow}
                  className="rounded-lg border border-orange-500/40 px-4 py-2 text-sm text-orange-400 hover:bg-orange-500/20 transition"
                >
                  Agregar orden de compra
                </button>

                <div className="text-xs text-muted">
                  Si una OC ya llego pero despues cambio el documento, puedes volver a subirlo en el mismo bloque.
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border/60 bg-background px-5 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-orange-500/40 px-4 py-2 text-sm text-orange-400 hover:bg-orange-500/20 transition"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  void save();
                }}
                disabled={saving}
                className="rounded-lg border border-orange-500/40 bg-orange-500/10 px-4 py-2 text-orange-300 hover:bg-orange-500/20 transition shadow-md disabled:opacity-60"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
